import { transition, trigger, useAnimation } from '@angular/animations';
import { Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { fadeIn } from '../shared/animations/animations';
import { ShellHeightToken } from '../shared/constants/shell-height.token';
import { MenuSelectItemEvent } from '../shared/directives/menu/menu-select-item.model';
import { MenuComponent } from '../shared/directives/menu/menu.component';
import { ContextMenu } from '../shared/models/context-menu.model';
import { ContextMenuService } from '../shared/services/context-menu.service';
import { DocumentService } from '../shared/services/document.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ContextService } from '../shared/shared-context/context.service';

@Component({
    selector: 'ohp-context-menu',
    templateUrl: './context-menu.component.html',
    animations: [
        trigger('open', [transition('* => open', useAnimation(fadeIn))])
    ]
})
export class ContextMenuComponent implements OnInit, OnDestroy {

    @ViewChild(MenuComponent)
    public set menuComponent(menuComponent: MenuComponent) {
        if (menuComponent && this.mobileMenusEnabled && this.focusOnOpen) {
            menuComponent.setActiveIndex(0, true);
        }
    }

    public menu: ContextMenu;

    public menuStyle: any;
    public mobileMenusEnabled: boolean;
    public antpLoaded: boolean;
    public animationState: 'open';
    public focusOnOpen: boolean;

    private contextMenuSubscription: Subscription;

    // TODO: This should be based on the height of the menu
    private static readonly ContextMenuBuffer = 300;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly contextMenuService: ContextMenuService,
        private readonly documentService: DocumentService,
        private readonly ngZone: NgZone,
        @Inject(ShellHeightToken) private readonly shellHeight: number
    ) {
    }

    public ngOnInit() {
        this.mobileMenusEnabled = this.capabilitiesService.capabilities.mobileContextMenusEnabled;
        this.antpLoaded = this.capabilitiesService.capabilities.antpLoaded;

        this.contextMenuSubscription = this.contextMenuService.subscribeToOpenEvents(newMenu => {
            if (!newMenu) {
                return;
            }

            this.animationState = 'open';
            this.focusOnOpen = newMenu.focusFirstItem;
            this.menu = newMenu;
            if (!this.mobileMenusEnabled) {
                this.ngZone.run(() => {
                    this.positionMenu();
                });
            }
        });
    }

    public ngOnDestroy() {
        if (this.contextMenuSubscription) {
            this.contextMenuSubscription.unsubscribe();
        }
    }

    public onSelectItem(event: MenuSelectItemEvent) {
        this.menu.menu.items[event.index].executeAction(event.nativeDomEvent);
        this.onCloseMenu();
    }

    public onCloseMenu() {
        this.contextMenuService.closeMenu(this.menu);
        this.animationState = null;
        this.focusOnOpen = false;

        const targetElement = document.getElementById(this.menu?.menu?.id);
        targetElement?.focus();
        this.menu = null;
    }

    private positionMenu() {
        const scrollContainer = this.documentService.nativeDocument.querySelector(this.antpLoaded ? '#antp__scroll-content' : '#officeHome__scroll-content');
        const scrollTop = scrollContainer.scrollTop;
        this.menuStyle = {
            'left.px': this.menu.position.left,
            'top.px': this.menu.position.top - this.shellHeight + scrollTop
        };
        const isRtl = this.contextService.isRtl;
        if ((this.menu.position.left < ContextMenuComponent.ContextMenuBuffer && (isRtl ? !this.menu.menu.rightAligned : this.menu.menu.rightAligned)) ||
            (this.menu.position.left > (scrollContainer.clientWidth - ContextMenuComponent.ContextMenuBuffer) && (isRtl ? this.menu.menu.rightAligned : !this.menu.menu.rightAligned))) {
            this.menu.menu.rightAligned = !this.menu.menu.rightAligned;
        }

        if (this.menu.position.top < ContextMenuComponent.ContextMenuBuffer && this.menu.menu.bottomAligned) {
            this.menu.menu.bottomAligned = false;
        } else if (this.menu.position.top > (scrollContainer.clientHeight + this.shellHeight - ContextMenuComponent.ContextMenuBuffer) && !this.menu.menu.bottomAligned) {
            this.menu.menu.bottomAligned = true;
        }
    }
}
